<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { PRODUCT_TYPE } from "@/util/constants";
import { generateFooterStyle } from "@/util/methods";
import { ArrowNarrowLeftIcon, ArrowNarrowRightIcon } from "@gohighlevel/ghl-icons/24/outline";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const props = defineProps({
  isLastSlideIndex: { type: Boolean, default: false },
  currentSlideIndex: { type: Number, default: 0 },
});

const store = useAppStore();

const showSubmitButton = computed(
  () =>
    props.isLastSlideIndex || (store.slides.length === 1 && !store.formAction.fieldSettingEnable)
);

// Quiz exposes a back-button toggle in settings; the canvas preview must honor it
const showPreviousButton = computed(
  () =>
    props.currentSlideIndex > 0 &&
    (store.product !== PRODUCT_TYPE.QUIZ || store.builderConfig.isBackButtonEnable)
);

// TODO: Add proper type definition for footer styles
const computedFooterStyles = computed(() => generateFooterStyle(store));
const footerStyle = computed(() =>
  store.deviceMode === "mobile" ? store.mobileFooterStyle : store.footerStyle
);

// Constants for layout spacing
const ARROW_SPACER_WIDTH = "w-[60px]";
</script>

<template>
  <div
    v-if="store.builderConfig?.newFooter"
    :style="computedFooterStyles.styles.footer"
    class="ghl-footer-preview ghl-footer footer-divider bottom-0 w-full"
  >
    <div
      v-if="
        footerStyle?.enableProgressBar &&
        footerStyle?.theme === 'steps' &&
        store.deviceMode !== 'mobile'
      "
      class="flex h-full items-center justify-center"
    >
      <span
        v-if="store.deviceMode !== 'mobile'"
        class="ghl-footer-progress-text mr-4 text-sm opacity-80"
        :style="computedFooterStyles.styles.progressBar.textStyle"
        >{{ t("common.showing") }} {{ currentSlideIndex + 1 }} {{ t("common.of") }}
        {{ store.slides.length }}</span
      >
      <div
        class="ghl-footer-progress-bar flex h-[10px] w-[320px] items-center rounded-lg"
        :style="computedFooterStyles.styles.progressBar.barStyle"
      >
        <div
          class="h-full rounded-l-lg bg-blue-400"
          :style="{
            width: `${((currentSlideIndex + 1) / store.slides.length) * 100}%`,
            ...computedFooterStyles.styles.progressBar.fillStyle,
          }"
        ></div>
      </div>
    </div>
    <div
      v-if="
        footerStyle?.enableProgressBar &&
        footerStyle?.theme === 'percentageBar' &&
        store.deviceMode !== 'mobile'
      "
      class="flex h-full items-center justify-center"
    >
      <div
        class="ghl-footer-progress-bar flex h-[24px] w-[200px] items-center rounded-lg"
        :style="computedFooterStyles.styles.progressBar.barStyle"
      >
        <div class="ghl-footer-progress-text absolute ml-px mt-px text-sm">0%</div>
        <div
          class="h-full rounded-none bg-blue-400"
          :style="{ width: '10%', ...computedFooterStyles.styles.progressBar.fillStyle }"
        ></div>
      </div>
    </div>

    <div
      class="ghl-footer-buttons z-2 absolute bottom-0 left-0 right-0 flex h-[18px] items-center"
      :style="computedFooterStyles.styles.container"
    >
      <div
        v-if="showPreviousButton"
        class="ghl-footer-back flex h-full select-none items-center p-4 relative top-[-1px] border-[0px] border-t border-solid border-[#eaecf0]"
        :style="computedFooterStyles.styles.backButton"
      >
        <ArrowNarrowLeftIcon
          v-if="computedFooterStyles.showArrow"
          direction="left"
          :stroke-color="computedFooterStyles.styles.arrowColor"
          class="ghl-footer-back-arrow mr-0.5 h-5 w-5"
          :style="computedFooterStyles.styles.icon"
        />
        <span v-if="computedFooterStyles.showText">{{
          footerStyle?.buttonStyle?.prevBtnText
        }}</span>
      </div>
      <div v-else class="w-[60px]"></div>

      <!-- Mobile slide indicator in center -->
      <div v-if="store.deviceMode === 'mobile'" class="flex flex-1 items-center justify-center">
        <span
          class="ghl-footer-progress-text text-sm opacity-80"
          :style="computedFooterStyles.styles.progressBar.textStyle"
          >{{ currentSlideIndex + 1 }} {{ t("common.of") }} {{ store.slides.length }}</span
        >
      </div>

      <div
        v-if="!showSubmitButton"
        class="ghl-footer-next flex h-full select-none items-center p-4 relative top-[-1px] border-[0px] border-r border-t border-solid border-[#eaecf0]"
        :style="computedFooterStyles.styles.nextButton"
      >
        <span v-if="computedFooterStyles.showText">{{
          footerStyle?.buttonStyle?.nextBtnText
        }}</span>
        <ArrowNarrowRightIcon
          v-if="computedFooterStyles.showArrow"
          direction="right"
          :stroke-color="computedFooterStyles.styles.arrowColor"
          class="ghl-footer-next-arrow ml-0.5 h-5 w-5"
          :style="computedFooterStyles.styles.icon"
        />
      </div>
      <div
        v-else
        class="ghl-footer-submit flex h-full select-none items-center p-4 relative top-[-1px] border-[0px] border-t border-solid border-[#eaecf0]"
        :style="computedFooterStyles.styles.submitButton"
      >
        <span>{{ footerStyle?.buttonStyle?.submitBtnText }}</span>
      </div>
    </div>
  </div>
</template>

<style>
.footer-divider {
  border-top: 1px solid #eaecf0;
}
</style>
